import React from "react";
import {AttachmentInformationProps} from "types/attachment";
import "./styles/attachmentInformation.scss";
import arrowDownDark from "components/taskdetails-remake/icons/arrow-down-dark.svg";
import arrowUpDark from "components/taskdetails-remake/icons/arrow-up-dark.svg";
import {taskAction} from 'components/mytasks/action/taskAction.action';
import {TASK_ACTIONS} from "utils/constants";
import {useSelector, useDispatch} from "react-redux";
import {Store} from "redux";
import * as reduxSelectors from "components/store/application.reducers";
import {snowplowTrackEvent} from "utils/snowplow";
import {ChevronDownIcon, ChevronUpIcon} from "lucide-react";
import {Button, ButtonType} from "components/common/Button.component";
import { useTranslation } from 'components/translations/useTranslation';

const AttachmentInformation = ({
                                   title,
                                   description,
                                   created,
                                   uploaderName,
                                   collapseCallback,
                                   isPanelCollapsed,
                                   canBeDeleted
                               }: AttachmentInformationProps): React.ReactElement => {


    const panelRef = React.useRef<HTMLDivElement>(null);
    const arrowRef = React.useRef<HTMLImageElement>(null);

    const taskKey = useSelector((store: Store) => reduxSelectors.getTaskDetails(store))?.key;
    const isPreviewDetached = useSelector((store: Store) => reduxSelectors.isDocumentPreviewDetached(store));
    const userData = useSelector((store: Store) => reduxSelectors.getUsersData(store));
    const dispatch = useDispatch();

    const { translate } = useTranslation();

    const toggleCollapsedState = () => {
        collapseCallback();
        panelRef.current?.classList.toggle("collapsed");
        const isPanelCollapsed = panelRef.current?.classList.contains("collapsed");

        if (arrowRef.current)
            arrowRef.current.src = isPanelCollapsed ? arrowDownDark : arrowUpDark;
    }

    const handleDeleteAttachment = () => {
        if (isPreviewDetached) {
            snowplowTrackEvent({
                category: "task_detail",
                action: "detach-window",
                label: "remove-attachment",
                value: "true",
            }, userData);
        }
        if (taskKey)
            dispatch(taskAction(TASK_ACTIONS.DELETE_ATTACHMENT, [taskKey]))

    }

    return (
        <div
            className={`attachment-information bg-white p-16 ${isPanelCollapsed ? "collapsed" : ""}`}
            ref={panelRef}>
            <div className={"d-flex align-content-center justify-content-between "}>

                   <span className={`attachment-information__title`}>
                    {title || ""}
                   </span>

                <div className={"float-right"}>
                    {isPanelCollapsed ?
                            <ChevronDownIcon className={"cursor-pointer collapse-button"}
                                             test-id="__tst_collapse_button"
                                             onClick={toggleCollapsedState}/> :
                            <ChevronUpIcon className={"cursor-pointer collapse-button"} onClick={toggleCollapsedState}
                                           est-id="__tst_uncollapse_button"/>

                    }

                </div>
            </div>

            {!isPanelCollapsed &&
                <div className={"d-flex justify-content-between align-items-center"}>
                    <div className={"d-flex flex-column"}>
                        <div>
                            <div
                                className={`attachment-information__description mt-3 mb-2`}>
                                {description || ""}
                            </div>


                            <div className={"attachment-information__delete_area d-flex justify-content-between"}>
                                <div className="attachment-information__name">
                                    {created ?
                                        translate("taskDetail.panels.attachmentInfo.uploadedByAndDate", created, uploaderName) :
                                        translate("taskDetail.panels.attachmentInfo.uploadedBy", uploaderName)
                                    }
                                </div>
                            </div>
                        </div>


                    </div>
                    {canBeDeleted &&
                        <Button type={ButtonType.GHOST}
                                onClick={handleDeleteAttachment}
                                disabled={!taskKey}
                                iconOnly={false}
                                text={translate("taskDetail.panels.attachmentInfo.remove")}
                                dataTestId={"__tst_maximize"}
                                tooltip={{id: "attachment-delete-tooltip", content: () => translate("taskDetail.actionBar.deleteAttachment")?.toString()}}
                        />
                    }
                </div>


            }
        </div>
    );

}

export default AttachmentInformation;